import { Backdrop, Box, Button, CircularProgress, Fade, Modal, Typography, useMediaQuery } from "@mui/material";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";
import { returnStringByLang } from "../../../utils";
import { useEffect, useState } from "react";
import PostService from "../../../services/postService";
import CustomInput from "../../inputs/customInput";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import OnOffSwitchForDeleted from "../../inputs/onOffSwitchForDeleted";

const ViewPostStatusModal = ({
    open,
    handleClose,
    postStatus,
    setPopupMsg,
    setPopupType,
    setShowPopup,
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [isLoading, setIsLoading] = useState(false);
    const [sendNotification, setSendNotification] = useState(!postStatus.sendNotification);
    const [sendMessage, setSendMessage] = useState(!postStatus.sendMessage);

    const [formValues, setFormValues] = useState({
        contentKurdish: postStatus.contentKurdish,
        contentEnglish: postStatus.contentEnglish,
        contentArabic: postStatus.contentArabic,
        order: postStatus.order,
        color: postStatus.color
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isLoading) return;

        try {
            setIsLoading(true);
            const data = await PostService.updatePostStatus(
                postStatus.id,
                {
                    sendNotification: !sendNotification,
                    sendMessage: !sendMessage,
                    ...formValues
                }
            );
            if (data.status === 200) {
                setPopupMsg(data.data.message);
                setPopupType('update');
                setShowPopup(true);
                setFormValues({
                    contentKurdish: '',
                    contentEnglish: '',
                    contentArabic: '',
                    order: ''
                });

                handleClose();
            }
        } catch (error) {
            if (error.response?.status === 400) {
                const responseErrors = error.response.data || {};
                const formattedErrors = {};

                Object.keys(responseErrors).forEach((field) => {
                    formattedErrors[field] = responseErrors[field];
                });

            } else {
                console.error("An unexpected error occurred:", error);
                setPopupMsg(error.response.data.error);
                setPopupType('error');
            }
        } finally {
            setIsLoading(false);
        }
    };



    useEffect(() => {
        if (postStatus) {
            setFormValues({
                contentKurdish: postStatus.contentKurdish || '',
                contentEnglish: postStatus.contentEnglish || '',
                contentArabic: postStatus.contentArabic || '',
                order: postStatus.order || ''
            });
            setSendNotification(!postStatus.sendNotification);
            setSendMessage(!postStatus.sendMessage);
        }
    }, [postStatus]);

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            aria-hidden="false"
            aria-modal="true"
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
        >
            <Fade in={open}>
                <Box
                    component="form"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: isTablet ? '90%' : '50%',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        height: '80%',
                        overflowY: 'scroll',
                        p: isTablet ? 2 : 4,
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        {/* title */}
                        <Typography id="transition-modal-title" variant="h6" component="h2">
                            {t('postStatusInfo')}
                        </Typography>
                        <CloseOutlinedIcon sx={{ cursor: 'pointer' }} onClick={handleClose} />
                    </Box>

                    {/* info text */}
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        {t('name')}: <span style={{ fontWeight: 'bold' }}>{returnStringByLang(postStatus, i18n.language)}</span>
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Typography variant="body1">
                            {t('color')}:
                        </Typography>
                        <Box width={2}></Box>
                        <input
                            type="color"
                            value={formValues.color || postStatus.color}
                            onChange={(e) =>
                                setFormValues((prevValues) => ({
                                    ...prevValues,
                                    color: e.target.value,
                                }))
                            }
                            style={{
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                width: '40%',
                                height: 30,
                                borderRadius: 10
                            }}
                        />
                    </Box>


                    <CustomInput
                        label={t('order')}
                        placeholder={t('orderHere')}
                        name="order"
                        type='text'
                        value={formValues.order || ''}
                        onChange={handleChange}
                    />

                    <CustomInput
                        label={t('contentKurdish')}
                        placeholder={t('contentKurdishHere')}
                        name="contentKurdish"
                        type='text'
                        value={formValues.contentKurdish || ''}
                        onChange={handleChange}
                    />

                    <CustomInput
                        label={t('contentArabic')}
                        placeholder={t('contentArabicHere')}
                        name="contentArabic"
                        type='text'
                        value={formValues.contentArabic || ''}
                        onChange={handleChange}
                    />

                    <CustomInput
                        label={t('contentEnglish')}
                        placeholder={t('contentEnglishHere')}
                        name="contentEnglish"
                        type='text'
                        value={formValues.contentEnglish || ''}
                        onChange={handleChange}
                    />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 2
                    }}>
                        <Typography variant="body1">
                            {t('sendNotification')}:&nbsp;&nbsp;
                        </Typography>
                        <OnOffSwitchForDeleted
                            checkDeleted={sendNotification}
                            setCheckDeleted={setSendNotification} />
                    </Box>


                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 2
                    }}>
                        <Typography variant="body1">
                            {t('sendMessage')}:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </Typography>
                        <OnOffSwitchForDeleted
                            checkDeleted={sendMessage}
                            setCheckDeleted={setSendMessage} />
                    </Box>



                    <Box sx={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        mt: 2
                    }}>
                        {/* close */}
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ textTransform: 'none', width: 140, height: 40 }}
                            onClick={handleClose}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('close')}</p>
                                <CloseOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        </Button>

                        <Box width={20}></Box>

                        <Box sx={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center'
                        }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none', width: 140, height: 40 }}
                                onClick={handleSubmit}
                            >
                                {isLoading ? <CircularProgress color="secondary" size={28} /> :
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <p>{t('update')}</p>
                                        <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                                    </Box>
                                }
                            </Button>
                        </Box>

                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}

export default ViewPostStatusModal;
